<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { ArrowLeft, Download } from 'lucide-vue-next'
import { CkAlert } from '@ck/ui'
import { reportsApi, workOrdersApi } from '@ck/api-client'

const router = useRouter()

// Não usa GET /reports (Manager-only): agrega client-side sobre as OS do próprio mecânico,
// cada uma já autorizada via GET /work-orders/{id}/reports.
const reportsQuery = useQuery({
  queryKey: ['reports-by-technician'],
  queryFn: async () => {
    const { items } = await workOrdersApi.list()
    const perOrder = await Promise.all(items.map(async (workOrder) => {
      const reports = await reportsApi.listByWorkOrder(workOrder.id)
      return reports.map((report) => ({ ...report, vehicle: workOrder.vehicle, workOrderNumber: workOrder.number }))
    }))
    return perOrder.flat().sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))
  }
})

const statusLabel: Record<string, string> = { queued: 'Na fila', ready: 'Pronto' }

async function download(id: string) {
  const blob = await reportsApi.download(id)
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 30000)
}
</script>

<template>
  <div class="app">
    <header>
      <button type="button" class="back" aria-label="Voltar" @click="router.push({ name: 'perfil' })"><ArrowLeft :size="20"/></button>
      <h1>Relatórios gerados</h1>
    </header>

    <CkAlert v-if="reportsQuery.isError.value" tone="error">Não foi possível carregar os relatórios.</CkAlert>
    <p v-else-if="reportsQuery.isPending.value" class="status-message">Carregando…</p>
    <p v-else-if="reportsQuery.data.value?.length === 0" class="status-message">Nenhum relatório gerado ainda.</p>
    <template v-else>
      <article v-for="report in reportsQuery.data.value" :key="report.id" class="card">
        <div class="head">
          <strong>{{ report.vehicle.brand }} {{ report.vehicle.model }}</strong>
          <span class="ck-code">{{ report.workOrderNumber }} · rev. {{ report.revision }}</span>
        </div>
        <div class="foot">
          <span class="badge" :class="`badge--${report.status}`">{{ statusLabel[report.status] ?? report.status }}</span>
          <button v-if="report.status === 'ready'" type="button" class="download" @click="download(report.id)"><Download :size="14"/>PDF</button>
        </div>
      </article>
    </template>
  </div>
</template>

<style scoped>
.app{max-width:620px;min-height:100vh;margin:auto;padding:calc(20px + env(safe-area-inset-top)) 18px 40px;background:var(--ck-cream-100)}
header{display:flex;align-items:center;gap:12px;margin-bottom:18px}
.back{display:flex;align-items:center;justify-content:center;width:38px;height:38px;flex-shrink:0;border:1px solid var(--ck-cream-300);border-radius:11px;background:var(--ck-cream-50);color:var(--ck-maroon-700);cursor:pointer}
header h1{margin:0;font-size:24px}
.status-message{padding:20px 0;color:var(--ck-text-muted);font-size:13px}
.card{padding:14px 16px;margin-bottom:10px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50)}
.head{display:flex;justify-content:space-between;align-items:baseline;gap:8px;margin-bottom:8px}
.head span{color:var(--ck-text-faint);font-size:11px}
.foot{display:flex;justify-content:space-between;align-items:center}
.badge{padding:3px 10px;border-radius:var(--radius-full);font-size:11px;font-weight:700;background:var(--ck-info-bg);color:var(--ck-info-text)}
.badge--ready{background:var(--ck-ok-bg);color:var(--ck-ok-text)}
.download{display:inline-flex;align-items:center;gap:5px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-sm);background:transparent;color:inherit;font-size:12px;padding:5px 10px;cursor:pointer}
</style>
